import { isEmptyObject, isNullOrUndefined } from '@as-react/common-utils';
import { createSelector } from 'reselect';
import { isSoldOutSize } from '../../../util/ProductUtil';
import { customSelectedColorSelectorCreator, customSelectedSkuDeliveryPromiseExtendedSelectorCreator, customSelectedSkuDeliveryPromiseSelectorCreator, customSelectedSkuSelectorCreator, makeGetAvailableColorsMemoizeSelectorCreator, productSelectorCreatorByActiveSizedForSelectedColor, productSelectorCreatorByColorAndSku, productSelectorCreatorByProductCode, productSelectorCreatorBySelectedColorId, } from './ProductCustomReSelector';
import SelectorUtil from './SelectorUtil';
const getProduct = (state) => state.product;
const getCompare = (state) => state.compare;
export const getSelectedColorId = (state) => state.product.selectedColorId;
export const getProductCode = (state) => state.product.productCode;
const getSelectedSku = (state) => state.product.selectedSku;
const getProductBrand = (state) => state.product.brand;
const getProductTranslatedProperties = (state) => state.product.productTranslatedProperties;
const getProductColors = (state) => state.product.productColorVariations;
const getProductSizes = (state) => {
    return state.product.productColorVariations.find(productColorVariation => productColorVariation.colorId === state.product.selectedColorId).sizes;
};
export const makeGetDetailInformation = () => productSelectorCreatorByProductCode([getProductBrand, getProductTranslatedProperties], (productBrand, productTranslatedProperties) => ({
    brand: productBrand,
    description: productTranslatedProperties.description,
    title: productTranslatedProperties.title,
    information: productTranslatedProperties.information,
    features: productTranslatedProperties.features,
    technicalDescription: productTranslatedProperties.technicalDescription,
}));
export const makeGetProductState = () => createSelector(getProduct, (product) => ({
    available: Boolean(product.active && product.visible),
    outOfStock: !!product.productColorVariations &&
        !product.productColorVariations.find((color) => Number(color.availability) > 0),
    selectedColorOutOfstock: !!product.productColorVariations &&
        !product.productColorVariations.find((color) => color.colorId === product.selectedColorId &&
            Number(color.availability) > 0 &&
            color.sizes.filter(size => size.active && Number(size.availability) > 0).length > 0),
}));
export const makeGetProductFulfillment = () => productSelectorCreatorByProductCode(getProduct, product => product.fulfillment);
export const makeGetProductInfo = () => createSelector(getProduct, (product) => {
    if (isNullOrUndefined(product))
        return undefined;
    return {
        defaultColor: product.defaultColor,
        productCode: product.productCode,
        productId: product.productId,
        selectedColorId: product.selectedColorId,
    };
});
export const makeGetProductSizes = () => productSelectorCreatorByActiveSizedForSelectedColor(getProductSizes, (productSizes) => productSizes.filter((size) => size.active || size.historicAvailable));
export const makeGetPriceInformation = () => productSelectorCreatorBySelectedColorId(getProduct, (product) => {
    const colorVariation = SelectorUtil.getSelectedColor(product);
    return SelectorUtil.getPriceInformation(product, colorVariation);
});
export const makeGetDynamicSavingsInformation = () => productSelectorCreatorByColorAndSku(getProduct, (product) => {
    const colorVariation = SelectorUtil.getSelectedColor(product);
    return SelectorUtil.getDynamicSavingsInformation(product, colorVariation);
});
export const makeGetProductDefaults = () => productSelectorCreatorByProductCode(getProduct, (product) => ({
    defaultColor: product.defaultColor,
    genderFeatureId: product.genderFeatureId,
    productCode: product.productCode,
    productId: product.productId,
    unavailableOnline: product.unavailableOnline,
}));
export const makeGetProductToggles = () => productSelectorCreatorByProductCode(getProduct, (product) => ({
    showQuantity: product.showQuantity,
}));
export const makeGetSelectedColorId = () => createSelector([getSelectedColorId], (colorId) => colorId);
export const makeGetSelectedSku = () => createSelector(getSelectedSku, (selectedSku) => selectedSku);
export const makeGetProduct = () => createSelector(getProduct, (product) => product);
export const makeGetSpecialtyType = () => createSelector(getProduct, (product) => { var _a; return (_a = product === null || product === void 0 ? void 0 : product.specialtyType) === null || _a === void 0 ? void 0 : _a.toLowerCase(); });
export const makeGetAvailableColors = () => makeGetAvailableColorsMemoizeSelectorCreator(getProduct, (product) => SelectorUtil.getAvailableColors(product));
export const makeGetColors = () => createSelector(getProductColors, (productColors) => productColors);
export const makeGetPreviewInformation = () => customSelectedColorSelectorCreator(getProduct, (product) => {
    const colorVariation = SelectorUtil.getSelectedColor(product);
    return {
        images: colorVariation.images,
        webOnly: colorVariation.webOnly,
        priceOverlay: colorVariation.priceOverlay,
    };
});
export const makeGetDeliveryPromises = () => customSelectedSkuDeliveryPromiseSelectorCreator(getProduct, (product) => product.deliveryPromise);
export const makeGetDeliveryPromisesExtended = () => customSelectedSkuDeliveryPromiseExtendedSelectorCreator(getProduct, (product) => product.deliveryPromiseExtended);
export const makeGetSizeCombinationNotExist = () => customSelectedSkuDeliveryPromiseSelectorCreator(getProduct, (product) => product.sizeCombinationNotExist);
export const makeGetSkuAvailability = () => customSelectedSkuSelectorCreator(getProduct, (product) => {
    const selectedColor = SelectorUtil.getSelectedColor(product);
    if (selectedColor) {
        const selectedSku = SelectorUtil.getSelectedSku(product, selectedColor);
        if (selectedSku) {
            return {
                lastPieces: selectedSku.lastPieces,
                exactAvailability: selectedSku.exactAvailability,
                isSoldOut: isSoldOutSize(selectedSku) && !selectedSku.storeSourceExcluded,
            };
        }
    }
    return {
        lastPieces: false,
        exactAvailability: null,
    };
});
export const makeGetBrandId = () => productSelectorCreatorByProductCode(getProduct, (product) => product && product.brandId);
export const makeGetSizeGuideInformation = () => productSelectorCreatorByProductCode(getProduct, (product) => ({
    brandId: product.brandId,
    genderFeatureId: product.genderFeatureId,
    productTypeFeatureId: product.productTypeFeatureId,
}));
export const makeGetProductVariants = () => productSelectorCreatorByProductCode(getProduct, (product) => product && product.productVariants);
const getSelectedColorVariation = (state) => SelectorUtil.getSelectedColor(state.product);
const getPPCTypeArg = (_, ppcType) => ppcType;
export const makeGetPPCForSelectedColorWithId = () => createSelector([getSelectedColorVariation, getPPCTypeArg], (colorVariation, ppcType) => {
    var _a;
    if (isNullOrUndefined(colorVariation))
        return null;
    return (_a = colorVariation.productPlacementCopyPositions) === null || _a === void 0 ? void 0 : _a.filter((ppc) => {
        return ppc.placementPositionId === ppcType;
    });
});
export const makeGetProductImageAlt = () => createSelector([getProduct, getSelectedColorVariation], (product, colorVariation) => {
    return `${product.brand.description} ${product.productTranslatedProperties.title} ${colorVariation.translations.description}`;
});
export const makeGetFirstImageForSelectedProductColor = () => createSelector([getSelectedColorVariation], (colorVariation) => {
    var _a;
    return (_a = colorVariation.images[0]) === null || _a === void 0 ? void 0 : _a.picture;
});
export const makeGetProductCompareInformation = () => customSelectedColorSelectorCreator(getProduct, (product) => {
    var _a;
    const colorVariation = SelectorUtil.getSelectedColor(product);
    return {
        brand: product.brand.description,
        productId: parseInt(product.productId, 10),
        productCode: product.productCode,
        title: product.productTranslatedProperties && 'title' in product.productTranslatedProperties
            ? product.productTranslatedProperties.title
            : '',
        seoUrl: product.seoUrl,
        colorId: colorVariation.colorId,
        image: (_a = colorVariation.images[0]) === null || _a === void 0 ? void 0 : _a.picture,
        prices: colorVariation.prices,
        hasDiscount: colorVariation.hasDiscount,
        discountPercentage: colorVariation.colourDiscountPercentage,
        colorDescription: colorVariation.translations.description,
        sizes: colorVariation.sizes,
        gender: product.gender,
        buyingGroup: product.buyingGroup,
        productQualifier: product.productQualifier,
        brandValue: product.brandQualifier,
    };
});
export const makeGetIsCompareProduct = () => createSelector([getProduct, getCompare], (product, compare) => {
    const foundProduct = compare.items.find((item) => product.productCode === item.productCode && product.selectedColorId === item.color);
    return !!foundProduct;
});
export const makeGetSizeRecommendation = () => createSelector([getProductTranslatedProperties, getSelectedColorVariation], (translatedProperty, colorVariation) => ({
    productBased: translatedProperty
        ? {
            modelSize: translatedProperty.modelSize,
            modelHeight: translatedProperty.modelHeight,
            sizingAdvice: translatedProperty.sizingAdvice,
        }
        : null,
    colorBased: (colorVariation === null || colorVariation === void 0 ? void 0 : colorVariation.translations)
        ? {
            modelSize: colorVariation.translations.modelSize,
            modelHeight: colorVariation.translations.modelHeight,
            sizingAdvice: colorVariation.translations.sizingAdvice,
        }
        : null,
}));
export const makeGetProductBrand = () => createSelector(getProductBrand, (productBrand) => productBrand);
export const makeGetProductDescription = () => createSelector([getProductBrand, getProductTranslatedProperties], (productBrand, productTranslatedProperties) => ({
    brand: productBrand,
    description: productTranslatedProperties.description,
    title: productTranslatedProperties.title,
}));
export const makeGetProductReview = () => productSelectorCreatorByProductCode(getProduct, (product) => product.productReview);
export const makeGetProductSustainable = () => productSelectorCreatorByProductCode(getProduct, (product) => product.sustainable);
export const makeGetProductOurPlanet = () => productSelectorCreatorByProductCode(getProduct, (product) => product.ourPlanet);
export const makeGetNewProduct = () => createSelector([getSelectedColorVariation], (colorVariation) => {
    return colorVariation.newProduct;
});
export const makeGetProductCertificates = () => productSelectorCreatorByProductCode(getProduct, (product) => product.productCertificates);
export const makeGetProductTechnicalCategories = () => productSelectorCreatorByProductCode(getProduct, (product) => product.productTechnicalCategories);
export const makeGetProductFeatures = () => productSelectorCreatorByProductCode(getProduct, (product) => product.productFeatures);
export const makeGetSelectedProductTab = () => createSelector(getProduct, product => product.selectedProductTab);
export const makeGetExpertReviews = () => productSelectorCreatorByProductCode(getProduct, ({ expertReviews, selectedColorId }) => {
    if (!(expertReviews === null || expertReviews === void 0 ? void 0 : expertReviews.length)) {
        return [];
    }
    return expertReviews
        .filter(({ colorId }) => Number(colorId) === selectedColorId || !colorId)
        .sort((a, b) => Number(b.colorId) - Number(a.colorId));
});
export const makeGetProductVideos = () => productSelectorCreatorByProductCode(getProduct, (product) => product.productVideos);
export const makeGetProductContentVideos = () => productSelectorCreatorByProductCode(getProduct, (product) => product.productContentVideos);
export const makeGetLinkedProducts = () => productSelectorCreatorByProductCode(getProduct, (product) => product.linkedProducts);
export const makeGetProductCareData = () => productSelectorCreatorByProductCode(getProduct, (product) => product.productCare);
export const makeGetSizeGuideData = () => productSelectorCreatorByProductCode(getProduct, (product) => ({
    sizeGuideContent: product.sizeGuideContent,
    sizeGuideContentUrl: product.sizeGuideContentUrl,
}));
export const makeGetProductFulfillmentBySize = () => createSelector([getSelectedColorVariation, getSelectedSku], (selectedColor, selectedSku) => {
    if (isNullOrUndefined(selectedColor) || isEmptyObject(selectedColor) || !selectedColor.sizes)
        return null;
    const selectedSize = selectedColor.sizes.find(size => size.sku === selectedSku);
    return {
        storeSourceExcluded: selectedSize ? selectedSize.storeSourceExcluded : false,
        fulfillmentCopy: selectedSize ? selectedSize.fulfillmentCopy : null,
    };
});
export const makeGetAlternativeProducts = () => productSelectorCreatorByProductCode(getProduct, (product) => product.alternativeProducts);
export const makeGetAlternativeProductsCampaignId = () => productSelectorCreatorByProductCode(getProduct, product => product.alternativeProductsCampaignId);
export const makeGetProductCategory = () => productSelectorCreatorByProductCode(getProduct, (product) => product.category);
export const makeGetBundle = () => customSelectedColorSelectorCreator(getProduct, (product) => { var _a; return (_a = product.bundles) === null || _a === void 0 ? void 0 : _a[product.selectedColorId]; });
export const makeGetBundleParentProduct = () => customSelectedColorSelectorCreator([getProduct, getSelectedColorVariation, getProductSizes], (product, colorVariation, productSizes) => {
    var _a;
    const bundle = (_a = product.bundles) === null || _a === void 0 ? void 0 : _a[product.selectedColorId];
    const title = product.productTranslatedProperties && 'title' in product.productTranslatedProperties
        ? product.productTranslatedProperties.title
        : '';
    return {
        activeColor: colorVariation,
        brand: product.brand.description,
        bundle,
        imageAlt: `${product.brand.description} ${title} ${colorVariation.translations.description}`,
        priceInformation: SelectorUtil.getPriceInformation(product, colorVariation),
        dynamicSavingsInformation: SelectorUtil.getDynamicSavingsInformation(product, colorVariation),
        selectedSku: product.selectedSku,
        seoUrl: product.seoUrl,
        sizes: productSizes.filter((size) => size.active || size.historicAvailable),
        title,
        review: product.productReview,
    };
});
export const makeGetBundleSelectedSlots = () => customSelectedColorSelectorCreator(getProduct, (product) => {
    var _a;
    const bundle = (_a = product.bundles) === null || _a === void 0 ? void 0 : _a[product.selectedColorId];
    return bundle === null || bundle === void 0 ? void 0 : bundle.bundleSlots.filter(slot => slot.isSelected).map(slot => `bundle-${bundle.bundleId}-${slot.slotNumber}`);
});
export const makeGetSelectedBundleAlternativeTab = () => customSelectedColorSelectorCreator(getProduct, (product) => { var _a, _b; return (_b = (_a = product.bundles) === null || _a === void 0 ? void 0 : _a[product.selectedColorId]) === null || _b === void 0 ? void 0 : _b.selectedTab; });
export const makeGetSelectedBundleProducts = () => customSelectedColorSelectorCreator(getProduct, (product) => {
    var _a;
    const bundle = (_a = product.bundles) === null || _a === void 0 ? void 0 : _a[product.selectedColorId];
    return (bundle === null || bundle === void 0 ? void 0 : bundle.bundleSlots.filter(slot => slot.isSelected).map(slot => slot.displayedProduct)) || [];
});
export const makeGetMainWebshopId = () => customSelectedColorSelectorCreator(getProduct, (product) => product.mainWebshopId);
export const makeGetIsSelectedSizeSoldOut = () => createSelector([getSelectedColorVariation, getSelectedSku], (selectedColor, selectedSku) => {
    const selectedSize = selectedColor.sizes.find(size => size.sku === selectedSku);
    return Boolean(selectedSize && isSoldOutSize(selectedSize) && !selectedSize.storeSourceExcluded);
});
export const makeGetNotifyMeError = () => productSelectorCreatorByProductCode(getProduct, (product) => product.notifyMeError);
